import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import useIsMobile from '../../hooks/useIsMobile';
import {
  BellIcon,
  UserCircleIcon,
  Bars3Icon,
  Squares2X2Icon,
  Cog6ToothIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  MegaphoneIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import bluelogo from '../../assets/bluelogo.png';

const Header = ({ isCollapsed, setIsCollapsed, onMobileMenuToggle, hideHelpdeskNav = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications } = useNotifications();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Bildirim tıklama handler'ı
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Bildirim tipine göre yönlendirme
    if (notification.ticket_id) {
      navigate(`/tickets/${notification.ticket_id}`);
    } else if (notification.project_id) {
      navigate(`/projects/${notification.project_id}`);
    }

    setIsNotificationOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Silently handle error
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 transition-all duration-200">
      <div className="w-full px-4 sm:px-6">
        <div className="flex justify-between items-center h-14 gap-3">

          {/* Sol taraf - Logo */}
          <div className="flex items-center min-w-0 md:min-w-[200px] lg:min-w-[240px] shrink-0">
            {!hideHelpdeskNav && (
            <button
              type="button"
              className="mr-1.5 p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 md:hidden"
              onClick={onMobileMenuToggle}
              aria-label="Menüyü aç"
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
            )}
            <div
              className="cursor-pointer group"
              onClick={() => {
                if (hideHelpdeskNav) {
                  navigate('/module-selection');
                  return;
                }
                if (isMobile) onMobileMenuToggle?.();
                else setIsCollapsed(!isCollapsed);
              }}
            >
              <img
                src={bluelogo}
                alt="Logo"
                className="h-9 md:h-10 w-auto object-contain transition-all duration-300 group-hover:scale-105"
              />
            </div>
          </div>

          {/* Orta - Search Bar (Mailchimp style) */}
          <div className="flex-1 max-w-2xl hidden lg:block">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                className="block w-full pl-9 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-md leading-5 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition-all duration-200"
                placeholder="Search..."
              />
            </div>
          </div>

          {/* Sağ taraf - Aksiyonlar ve Profil */}
          <div className="flex items-center space-x-2 md:space-x-4">


            {/* Diğer Fonksiyonel İkonlar */}
            <div className="hidden sm:flex items-center space-x-1 border-r border-gray-200 dark:border-gray-700 pr-2 md:pr-4">
              <button
                onClick={() => navigate('/module-selection')}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all"
                title="Modül Seçimi"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/team')}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all"
                title="Team"
              >
                <UsersIcon className="h-4 w-4" />
              </button>

              <button
                onClick={() => navigate('/changelog')}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all"
                title="Yenilikler"
              >
                <MegaphoneIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Bildirim Butonu */}
            <button
              onClick={() => navigate('/module-selection')}
              className="sm:hidden p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all"
              title="Modül Seçimi"
              aria-label="Modül Seçimi"
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>

            <div className="relative">
              <button
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full relative transition-all"
              >
                <BellIcon className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border-2 border-white dark:border-gray-800">
                    {unreadCount}
                  </span>
                )}
              </button>
              {/* ... Notifications dropdown remains same ... */}

              {/* Bildirim Dropdown */}
              {isNotificationOpen && (
                <div className={`absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 ${isMobile ? 'w-[92vw] max-w-[360px]' : 'w-80'}`}>
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Bildirimler
                    </h3>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        Henüz bildirim yok
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${!notification.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-lg">{getNotificationIcon(notification.notification_type)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {notification.title}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {notification.time_ago}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        navigate('/notifications');
                        setIsNotificationOpen(false);
                      }}
                      className="w-full text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      Tüm bildirimleri görüntüle
                    </button>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={refreshNotifications}
                        className="flex-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        🔄 Yenile
                      </button>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="flex-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          Tümünü okundu işaretle ({unreadCount})
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Profil Butonu */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-1.5 p-1.5 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <UserCircleIcon className="h-5 w-5" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Admin
                </span>
              </button>

              {/* Profil Dropdown */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-1">
                    {/* Profil */}
                    <button
                      onClick={() => {
                        // Mevcut modülü kaydet
                        const currentModule = location.pathname.startsWith('/stopsale') ? 'stopsale' : 'helpdesk';
                        localStorage.setItem('lastActiveModule', currentModule);
                        navigate('/profile');
                        setIsProfileOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <UserIcon className="h-4 w-4 mr-3 text-gray-500" />
                      Profil
                    </button>

                    {/* Ayarlar */}
                    <button
                      onClick={() => {
                        // Mevcut modülü kaydet
                        const currentModule = location.pathname.startsWith('/stopsale') ? 'stopsale' : 'helpdesk';
                        localStorage.setItem('lastActiveModule', currentModule);
                        navigate('/settings');
                        setIsProfileOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Cog6ToothIcon className="h-4 w-4 mr-3 text-gray-500" />
                      Ayarlar
                    </button>

                    {/* Ayırıcı */}
                    <hr className="my-1 border-gray-200 dark:border-gray-700" />

                    {/* Çıkış Yap */}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsProfileOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-red-500" />
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay - Dropdown'lar açıkken arka planı karartmak için */}
      {(isNotificationOpen || isProfileOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsNotificationOpen(false);
            setIsProfileOpen(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;
